import React, { useState, useEffect } from 'react';
import GlobalDataStore from '../utils/GlobalDataStore';

const BurnHistory = () => {
  const [burnHistory, setBurnHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [transactionDetails, setTransactionDetails] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState({});

  useEffect(() => {
    loadBurnHistory();
    
    // Listen for burn data updates from GlobalDataStore
    const unsubscribe = GlobalDataStore.subscribe(() => {
      loadBurnHistory();
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const loadBurnHistory = () => {
    setIsLoading(true);
    
    try {
      // Get detailed burn data from GlobalDataStore
      const analyticsData = GlobalDataStore.getBurnPoolAnalyticsData();
      
      if (!analyticsData.burnsByDay || Object.keys(analyticsData.burnsByDay).length === 0) {
        // No detailed data available yet
        setBurnHistory([]);
        setIsLoading(false);
        return;
      }

      // Process burn data into history array - show all time data
      const historyArray = Object.entries(analyticsData.burnsByDay)
        .map(([timestamp, amount]) => ({
          date: new Date(parseInt(timestamp) * 1000),
          amount: parseFloat(amount),
          timestamp: parseInt(timestamp)
        }))
        .sort((a, b) => b.timestamp - a.timestamp) // Most recent first
        .slice(0, 10); // Show last 10 entries

      setBurnHistory(historyArray);
      setIsLoading(false);
    } catch (error) {
      setBurnHistory([]);
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const toggleRowExpansion = async (timestamp) => {
    const newExpanded = new Set(expandedRows);
    
    if (newExpanded.has(timestamp)) {
      // Already expanded, just collapse it
      newExpanded.delete(timestamp);
      setExpandedRows(newExpanded);
    } else {
      // Need to expand - fetch transactions if not already fetched
      if (!transactionDetails[timestamp]) {
        await fetchTransactionDetails(timestamp);
      }
      newExpanded.add(timestamp);
      setExpandedRows(newExpanded);
    }
  };

  const fetchTransactionDetails = async (timestamp) => {
    // Set loading state for this row
    setLoadingTransactions(prev => ({ ...prev, [timestamp]: true }));
    
    try {
      const dayStart = timestamp - (timestamp % (24 * 60 * 60));
      const dayEnd = dayStart + (24 * 60 * 60);
      
      // Fetch transactions for this specific day
      const url = `https://sds0.steemworld.org/rewards_api/getRewards/comment_benefactor_reward/null/${dayStart}-${dayEnd}/5000/0`;
      
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('Failed to fetch transactions');
      
      const result = await resp.json();
      const allRows = result.result?.rows || [];
      
      // Filter only rows where author is steemburnup (our tracking account)
      const rows = allRows.filter(row => row[1] === 'steemburnup');
      
      // Get VESTS conversion rate for accurate STEEM calculation
      const globalResp = await fetch('https://api.steemit.com', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'condenser_api.get_dynamic_global_properties',
          params: [],
          id: 1
        })
      });
      
      const globalData = await globalResp.json();
      const totalVestingShares = parseFloat(globalData.result.total_vesting_shares.split(' ')[0]);
      const totalVestingFundSteem = parseFloat(globalData.result.total_vesting_fund_steem.split(' ')[0]);
      const steemPerVest = totalVestingFundSteem / totalVestingShares;
      
      // Get SBD to STEEM conversion rate
      let sbdToSteem = 1.0;
      try {
        const priceResp = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=steem,steem-dollars&vs_currencies=usd');
        if (priceResp.ok) {
          const priceData = await priceResp.json();
          const steemPrice = priceData.steem?.usd;
          const sbdPrice = priceData['steem-dollars']?.usd;
          if (steemPrice && sbdPrice) {
            sbdToSteem = sbdPrice / steemPrice;
          }
        }
      } catch (e) {
        // Use default conversion
      }
      
      // Process transactions
      const transactions = rows
        .map(row => {
          const txTimestamp = parseInt(row[0]);
          const author = row[1]; // Author who posted
          const permlink = row[2]; // Permlink
          const sbd = parseFloat(row[3]) || 0;
          const steem = parseFloat(row[4]) || 0;
          const vests = parseFloat(row[5]) || 0;
          
          const vestsSteemValue = vests * steemPerVest;
          const sbdSteemValue = sbd * sbdToSteem;
          const burnAmount = steem + sbdSteemValue + vestsSteemValue;
          
          // Construct post URL from author and permlink
          let postUrl = '';
          if (author && permlink) {
            postUrl = `https://steemit.com/@${author}/${permlink}`;
          }
          
          return {
            timestamp: txTimestamp,
            date: new Date(txTimestamp * 1000),
            steem: steem,
            sbd: sbd,
            vests: vests,
            vestsSteemValue: vestsSteemValue,
            sbdSteemValue: sbdSteemValue,
            totalAmount: burnAmount,
            author: author,
            permlink: permlink,
            postUrl: postUrl
          };
        })
        .sort((a, b) => b.timestamp - a.timestamp);
      
      setTransactionDetails(prev => ({
        ...prev,
        [timestamp]: transactions
      }));
    } catch (error) {
      setTransactionDetails(prev => ({
        ...prev,
        [timestamp]: []
      }));
    } finally {
      setLoadingTransactions(prev => ({ ...prev, [timestamp]: false }));
    }
  };

  return (
    <div className="burn-history-container">
      <div className="burn-history-header">
        <div className="header-left">
          <div className="icon-container">
            <div className="icon-glow"></div>
            <i className="fas fa-history"></i>
          </div>
          <div className="header-text">
            <h3 className="card-title">Burn History</h3>
            <p className="card-subtitle">Recent burning activity to @null</p>
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="burn-history-table-container">
        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading burn history...</p>
          </div>
        ) : burnHistory.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox" style={{fontSize: '3rem', color: 'rgba(255,255,255,0.3)', marginBottom: '16px'}}></i>
            <p>No burn history available</p>
            <small>Data is collected from recent burn activities</small>
          </div>
        ) : (
          <div className="burn-history-table">
            <div className="table-header">
              <div className="header-cell">Date</div>
              <div className="header-cell">Amount Burned</div>
              <div className="header-cell">Status</div>
            </div>
            <div className="table-body">
              {burnHistory.map((entry, index) => (
                <div key={entry.timestamp}>
                  <div 
                    className="table-row clickable"
                    onClick={() => toggleRowExpansion(entry.timestamp)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="table-cell" data-label="Date">
                      <i className="fas fa-calendar-alt" style={{marginRight: '8px', color: '#4BA2F2'}}></i>
                      {formatDate(entry.date)}
                    </div>
                    <div className="table-cell amount-cell" data-label="Amount Burned">
                      <i className="fas fa-fire" style={{marginRight: '8px', color: '#FF6B6B'}}></i>
                      <span className="amount-value">{formatAmount(entry.amount)} STEEM</span>
                    </div>
                    <div className="table-cell status-cell" data-label="Status">
                      <span className="status-badge">
                        <i className="fas fa-check-circle"></i> Burned
                      </span>
                      <i 
                        className={`fas fa-chevron-down expansion-icon ${expandedRows.has(entry.timestamp) ? 'expanded' : ''}`}
                        style={{marginLeft: '8px'}}
                      ></i>
                    </div>
                  </div>
                  
                  {/* Expanded details section */}
                  {expandedRows.has(entry.timestamp) && (
                    <div className="transaction-details-container">
                      {loadingTransactions[entry.timestamp] ? (
                        <div className="transaction-loading">
                          <div className="small-spinner"></div>
                          <p>Loading transactions...</p>
                        </div>
                      ) : transactionDetails[entry.timestamp]?.length > 0 ? (
                        <div className="transactions-list">
                          <div className="transactions-header">
                            <span>{transactionDetails[entry.timestamp].length} Transaction{transactionDetails[entry.timestamp].length !== 1 ? 's' : ''}</span>
                          </div>
                          {transactionDetails[entry.timestamp].map((tx, txIndex) => (
                            <div key={txIndex} className="transaction-item">
                              <div className="transaction-time">
                                {tx.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                              </div>
                              <div className="transaction-breakdown">
                                {tx.steem > 0 && (
                                  <div className="transaction-component">
                                    <span className="component-label">STEEM:</span>
                                    <span className="component-value">{formatAmount(tx.steem)}</span>
                                  </div>
                                )}
                                {tx.vests > 0 && (
                                  <div className="transaction-component">
                                    <span className="component-label">VESTS:</span>
                                    <span className="component-value">{formatAmount(tx.vests)} VESTS</span>
                                    <span className="component-equiv">({formatAmount(tx.vestsSteemValue)} STEEM)</span>
                                  </div>
                                )}
                                {tx.sbd > 0 && (
                                  <div className="transaction-component">
                                    <span className="component-label">SBD:</span>
                                    <span className="component-value">{formatAmount(tx.sbd)} SBD</span>
                                    <span className="component-equiv">({formatAmount(tx.sbdSteemValue)} STEEM)</span>
                                  </div>
                                )}
                              </div>
                              <div className="transaction-total">
                                Total: <strong>{formatAmount(tx.totalAmount)}</strong> STEEM
                              </div>
                              {tx.postUrl && (
                                <div className="transaction-post-link">
                                  <a href={tx.postUrl} target="_blank" rel="noopener noreferrer">
                                    <i className="fas fa-external-link-alt"></i> View Post
                                  </a>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-transactions">
                          <p>No transactions found for this day</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BurnHistory;
